import { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { app } from "app/Firebase";
import { getApiUrl } from "lib/apiUrl";
import { getTenantId } from "./tenant";

type ConsentDetails = {
  clientName: string;
  redirectUri: string;
  scope: string;
};

function readDetails(value: unknown): ConsentDetails {
  if (
    typeof value !== "object" ||
    value === null ||
    !("clientName" in value) ||
    typeof value.clientName !== "string" ||
    !("redirectUri" in value) ||
    typeof value.redirectUri !== "string" ||
    !("scope" in value) ||
    typeof value.scope !== "string"
  )
    throw new Error("The connection request could not be read.");
  const destination = new URL(value.redirectUri);
  if (
    destination.username ||
    destination.password ||
    !(
      destination.protocol === "https:" ||
      (destination.protocol === "http:" &&
        ["localhost", "127.0.0.1", "[::1]"].includes(destination.hostname))
    )
  )
    throw new Error(
      "The connection request has an unsupported return address.",
    );
  return {
    clientName: value.clientName,
    redirectUri: value.redirectUri,
    scope: value.scope,
  };
}

export function McpConsent({
  request,
  onRedirect = (url: string) => window.location.assign(url),
}: {
  request: string;
  onRedirect?: (url: string) => void;
}) {
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [identity, setIdentity] = useState<{
    uid: string;
    email: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setDetails(null);
    setIdentity(null);
    setError(null);
    setSubmitting(false);
    async function load() {
      if (!/^[a-f0-9]{64}$/.test(request))
        throw new Error(
          "This connection request is missing or invalid. Start again from your MCP client.",
        );
      const user = getAuth(app).currentUser;
      if (!user)
        throw new Error("Please sign in again to connect this client.");
      const token = await user.getIdToken();
      const response = await fetch(
        getApiUrl(
          `/api/mcp/oauth/request?request=${encodeURIComponent(request)}`,
        ),
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        },
      );
      if (!response.ok)
        throw new Error(
          "This connection request is unavailable or expired. Start again from your MCP client.",
        );
      const value: unknown = await response.json();
      const loaded = readDetails(value);
      if (!cancelled) {
        setDetails(loaded);
        setIdentity({ uid: user.uid, email: user.email ?? user.uid });
      }
    }
    void load().catch((cause: unknown) => {
      if (!cancelled)
        setError(
          cause instanceof Error
            ? cause.message
            : "Unable to load the connection request.",
        );
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [request]);

  async function decide(approved: boolean) {
    if (!details || !identity || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const user = getAuth(app).currentUser;
      if (!user || user.uid !== identity.uid)
        throw new Error(
          "Your signed-in account changed. Reload this page before continuing.",
        );
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl("/api/mcp/oauth/consent"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ request, approved }),
      });
      if (!response.ok)
        throw new Error(
          "Unable to complete this request. It may have expired; start again from your MCP client.",
        );
      const value: unknown = await response.json();
      if (
        typeof value !== "object" ||
        value === null ||
        !("redirectUrl" in value) ||
        typeof value.redirectUrl !== "string"
      ) {
        throw new Error("The server returned an invalid return address.");
      }
      const destination = new URL(value.redirectUrl);
      const expected = new URL(details.redirectUri);
      if (
        destination.origin !== expected.origin ||
        destination.pathname !== expected.pathname ||
        destination.username ||
        destination.password
      ) {
        throw new Error(
          "The return address changed. Start again from your MCP client.",
        );
      }
      onRedirect(destination.href);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Unable to complete the connection request.",
      );
      setSubmitting(false);
    }
  }

  return (
    <section
      aria-labelledby="mcp-consent-title"
      style={{
        background: "white",
        color: "#182026",
        padding: 28,
        margin: 16,
        borderRadius: 12,
        maxWidth: 580,
        overflowWrap: "anywhere",
      }}
    >
      <h1 id="mcp-consent-title">Connect to Job Manager</h1>
      {!details && !error && <p role="status">Loading connection request…</p>}
      {details && identity && (
        <>
          <p>
            <strong>{details.clientName}</strong> is requesting read-only access
            to Job Manager.
          </p>
          <p>
            It can inspect available queries and types, and read data allowed by
            your existing permissions. It cannot run mutations.
          </p>
          <dl>
            <dt>Signed in as</dt>
            <dd>{identity.email}</dd>
            <dt>Tenant</dt>
            <dd>{getTenantId()}</dd>
            <dt>Requested scope</dt>
            <dd>{details.scope}</dd>
            <dt>Return address</dt>
            <dd>{details.redirectUri}</dd>
          </dl>
          <p>
            Approve only if you started this connection and trust this client
            with the data you can access.
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void decide(false)}
            >
              Deny
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void decide(true)}
            >
              Approve
            </button>
          </div>
          {submitting && <p role="status">Returning to the client…</p>}
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
